import type { ProfileModuleDto } from '@up2stay/up2promo-api-client';
import { toPersistedGoogleMapsConfig } from '@up2stay/up2promo-commons/profile';

import { toStoredFileKey } from '@/core/lib/file-url';

function isFileLike(value: unknown): value is { id: string; path: string } {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.id === 'string' &&
    record.id.length > 0 &&
    typeof record.path === 'string' &&
    record.path.length > 0
  );
}

/** Persist nested `{ id, path }` as storage keys — never signed GET URLs. */
function persistFileRefsInValue<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((item) => persistFileRefsInValue(item)) as T;
  }
  if (!value || typeof value !== 'object') {
    return value;
  }
  if (isFileLike(value)) {
    return {
      ...value,
      path: toStoredFileKey(value.path),
    } as T;
  }
  const entries = Object.entries(value as Record<string, unknown>).map(
    ([key, child]) => [key, persistFileRefsInValue(child)] as const,
  );
  return Object.fromEntries(entries) as T;
}

function persistModuleConfig(
  type: ProfileModuleDto['type'],
  config: ProfileModuleDto['config'],
): ProfileModuleDto['config'] {
  const persistedFiles = persistFileRefsInValue(config ?? {});
  if (type === 'google_maps') {
    return toPersistedGoogleMapsConfig(
      persistedFiles as Record<string, unknown>,
    );
  }
  return persistedFiles;
}

/**
 * Whitelists writable module fields for create/update DTOs.
 * Response modules include read-only `profileId` (and may carry other extras)
 * which `forbidNonWhitelisted` rejects.
 *
 * Nested file objects keep `id` + storage key only (DEV-458).
 * Google Maps modules keep `locationId` only (no address/coords snapshot).
 */
export function sanitizeModulesForProfileApi(
  modules: ProfileModuleDto[],
): ProfileModuleDto[] {
  return modules.map((module, index) => {
    const payload: ProfileModuleDto = {
      type: module.type,
      order: module.order ?? index,
      enabled: module.enabled ?? true,
      config: persistModuleConfig(module.type, module.config),
    };
    if (module.style && Object.keys(module.style).length > 0) {
      payload.style = module.style;
    }
    const moduleId = module.id != null ? String(module.id).trim() : '';
    if (moduleId) {
      payload.id = moduleId;
    }
    return payload;
  });
}
